/* eslint-disable import/no-extraneous-dependencies */
import type { History } from 'history';
import pick from 'lodash/pick';
import { applyMiddleware, combineReducers, createStore, Store } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { createLogger } from 'redux-logger';
import createSagaMiddleware from 'redux-saga';
import rollbar from 'lib/ErrorLogger';
import { accountRecoveryReducer } from 'store/AccountRecovery';
import { apiReducer } from 'store/ApiClient';
import { App, appReducer, loadAppState, persistAppState } from 'store/App';
import { calculatorRulesReducer } from 'store/CalculatorRules';
import { achReducer, alertsReducer, employeePayrollTipsReducer, employeesReducer, jobsReducer, ledgerReducer, paymentsReducer, payoutsReducer, payPeriodsReducer, posReducer } from 'store/Collections';
import { daySegmentsReducer } from 'store/Collections/DaySegments';
import { integrationAppsReducer, siteIntegrationsReducer } from 'store/Collections/Integrations';
import { employeeReducer } from 'store/Employee';
import { employersReducer } from 'store/Employers';
import { integrationReducer } from 'store/Integration';
import { invitesReducer } from 'store/Invites';
import { laborReviewReducer } from 'store/LaborReview';
import { paymentReducer } from 'store/Payment/reducer';
import { rootSaga } from 'store/root/sagas';
import { signUpUserReducer } from 'store/SignUp';
import { siteReducer } from 'store/Site';
import { statsReducer } from 'store/Stats';
import { tipCalculatorReducer } from 'store/TipCalculator';
import { ICollectionState, IStore } from 'store/types';
import { userReducer } from 'store/User';
import { IAction } from 'types';
import { isDevelopment } from 'utils';

declare global {
  interface Window {
    Cypress: any;
    store: any;
    browserHistory: History;
  }
}

const allReducers = combineReducers<IStore>({
  api: apiReducer,
  app: appReducer,
  accountRecovery: accountRecoveryReducer,
  collections: combineReducers<ICollectionState>({
    jobs: jobsReducer,
    ach: achReducer,
    alerts: alertsReducer,
    payPeriods: payPeriodsReducer,
    ledger: ledgerReducer,
    daySegments: daySegmentsReducer,
    employeePayrollTips: employeePayrollTipsReducer,
    payments: paymentsReducer,
    payouts: payoutsReducer,
    pos: posReducer,
    employees: employeesReducer,
    integrationApps: integrationAppsReducer,
    siteIntegrations: siteIntegrationsReducer,
  }),
  calculatorRules: calculatorRulesReducer,
  tipCalculator: tipCalculatorReducer,
  employers: employersReducer,
  employee: employeeReducer,
  payment: paymentReducer,
  stats: statsReducer,
  site: siteReducer,
  integration: integrationReducer,
  invite: invitesReducer,
  laborReview: laborReviewReducer,
  signUpUser: signUpUserReducer,
  user: userReducer,
});

// What a profile switch keeps: state not tied to the org or site being viewed.
const SESSION_STATE = ['api', 'app', 'accountRecovery', 'employers', 'invite', 'signUpUser', 'user', 'collections.alerts', 'collections.integrationApps'];

export const rootReducer = (originalState: IStore, action: IAction) => {
  // A reducer given undefined returns its initial state, so any slice left out starts fresh.
  if (action.type === App.LOGOUT_SUCCESS) return allReducers(undefined, action);
  if (action.type === App.SWITCH_PROFILE) return allReducers(pick(originalState, SESSION_STATE) as IStore, action);
  return allReducers(originalState, action);
};

const endsWith = (action: IAction, suffix: string) => String(action?.type).endsWith(suffix);

const storeLogger = () =>
  createLogger({
    collapsed: (_getState, action, logEntry) => !logEntry?.error && !endsWith(action, 'FAIL'),
    duration: true,
    level: { prevState: 'log', action: 'log', nextState: 'log', error: 'error' },
    // The diff shows whether a reducer changed anything; `*_REQUEST` only flips a loading flag.
    diff: true,
    diffPredicate: (_getState, action) => !endsWith(action, 'REQUEST'),
  });

export const configureStore = (): Store<IStore> => {
  const composeEnhancers = composeWithDevTools({});
  const sagaMiddleware = createSagaMiddleware({
    onError: (err) => {
      if (!isDevelopment()) {
        rollbar.critical(err);
      }
    },
  });

  const middlewares: any = [];
  middlewares.push(sagaMiddleware);
  if (isDevelopment()) middlewares.push(storeLogger());

  const store = createStore(rootReducer, { app: loadAppState() } as IStore, composeEnhancers(applyMiddleware(...middlewares)));

  // Run the root saga
  sagaMiddleware.run(rootSaga);

  persistAppState(store);

  // Expose store when run in Cypress
  if (window.Cypress) window.store = store;

  return store;
};
